/**
 * starting-copy — the boot words, owned in one place.
 *
 * Two live surfaces narrate the gap between "sent" and "the agent is moving":
 * the task page's status line (message-list/lifecycle.ts) and the composer's
 * fleet row (components/composer/fleet.ts). They must say the SAME thing at the
 * same beat — the founder's 08-19 report was the row saying "Queued" while the
 * page said "Waking…" about the identical task. The predicates stay with each
 * surface (they read different stores); the copy lives here so a reword can
 * never land on one surface and not the other.
 *
 * No imports on purpose: a pure mapper (fleet.ts) must be able to take this
 * without pulling a React component graph in behind it.
 */

/**
 * The boot label names the thing that is actually waking. On the web the turn
 * runs on the user's cloud workspace; inside the desktop shell it runs on their
 * own machine (see useWarmupStatus's placement).
 *
 * NOT DEAD CODE: the 'local' copy is unreachable on the task page's rail today
 * BY DESIGN — warmingFor() returns false for local placement, because a
 * local-lane turn never waits on the cloud sandbox coming up, so `warming` is
 * never true there. It is kept for the day the desktop shell reports its OWN
 * runner boot through this same rail; deleting it would have to be undone then.
 */
export const STARTING_LABEL: Record<"cloud" | "local", string> = {
  cloud: "Waking your workspace…",
  local: "Waking your computer…",
};

/**
 * Presence is READY and the message is on its way to the runner: the turn has
 * not opened, so nothing is "thinking" yet. Same visual family as the waking
 * line.
 */
export const DISPATCHING_LABEL = "Starting…";

/**
 * A plan engine's sign-in step (engines T2a; every plan engine since T3).
 * Before the turn opens, the box opens the vendor's own sign-in and waits: the
 * wait is on the PERSON, so the line says so rather than "Connecting…", which
 * read as the box doing the work. Founder ruling R9 lets this line name the
 * plan: it names the account the person is asked to sign in to.
 *
 * `plan` is the plan's name for the task's own engine ("Claude", "ChatGPT":
 * ENGINE_PLAN_NAME, through planNameOf in lib/engine-plans.ts), passed by the
 * caller so this module still imports nothing.
 *
 * Three renderers say it: the task page's status line (sentence case), the
 * task story's desk ("Bash is <verb>", lower case) and the composer's fleet row.
 */
export const signInWaitVerb = (plan: string) =>
  `waiting for you to sign in to ${plan}`;
export const signInWaitLabel = (plan: string) =>
  `Waiting for you to sign in to ${plan}`;

/**
 * An engine's first use on a computer that does not have it yet (the runner's
 * AGENT_STATUS 'engine_installing'; Codex installs on first use since 10-06):
 * the box downloads and checks the engine before anything can start, which can
 * take a minute or more with no step to show. Unlike the sign-in it names no
 * plan: the person chose a plan, not a program, so the line stays engine
 * neutral. The same three renderers say it as the sign-in line.
 */
export const ENGINE_INSTALLING_VERB = "setting up the engine on your computer";
export const ENGINE_INSTALLING_LABEL =
  "Setting up the engine on your computer…";

/**
 * The platform will not start the workspace and the server is retrying it on a backoff
 * (server sandbox/lifecycle/unreachable.ts, the `unreachable` repair on presence). A
 * whole sentence, not a verb: the person needs to know two things, that it is not them
 * and that they need not do anything. The header's subtitle and the desk say the same.
 */
export const WORKSPACE_TROUBLE_SENTENCE =
  "Your workspace is having trouble starting. We'll keep trying and continue automatically.";
